// Settings → Bundles panel (#100). Export and import the active
// org's config as a single JSON file. Same shape as the Vault
// starter pack so bundles work as backups, as moves between orgs,
// and as preset templates.
//
// Export: pick which of the four resource types to include, click
// Download. Server returns a fresh bundle each call.
// Import: paste JSON or upload a .json file → server runs a dryRun
// first, shows what WOULD happen, then commits on confirm.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BundleDiffEntry, BundleInstallEvent, Nest } from '@captain-tasko/shared';
import { BundleV2Schema } from '@captain-tasko/shared';
import hljs from 'highlight.js/lib/core';
import jsonLang from 'highlight.js/lib/languages/json';
import 'highlight.js/styles/github.css';
import { apiList, apiSingle } from '../lib/api.js';
import { toast } from './Toast.js';

// CTSK-61 — register json language once for the pre-flight tree viewer.
// highlight.js/lib/core is import-safe to call at module load; the language
// registration is a no-op if already present.
hljs.registerLanguage('json', jsonLang);

const RESOURCE_TYPES = [
  { value: 'containers',        label: 'Storage containers',   defaultOn: true  },
  { value: 'tags',              label: 'Tags',                 defaultOn: true  },
  { value: 'rules',             label: 'Tagging rules',        defaultOn: true  },
  { value: 'file-workflows',    label: 'File workflows',       defaultOn: true  },
  // #119 — work-item workflow definitions (state machines). The
  // server's `?types=workflows` exports the workflow_definitions
  // table; import does name-uniqueness only since states/transitions
  // are self-contained JSONB (no cross-resource refs).
  { value: 'workflows',         label: 'Work-item workflows',  defaultOn: true  },
  // 6th type — custom property definitions. Mirrors #119's simplicity:
  // self-contained, name-uniqueness only on import (via (entity, key)).
  { value: 'custom-properties', label: 'Custom properties',    defaultOn: true  },
  // #128 — projects + work items. Default-OFF because bundles balloon
  // as orgs accrue work items; operators opt in for nest seed bundles
  // and full org backups. Work items in bundles are SEED INSTANCES —
  // real rows, idempotent re-import via externalId.
  { value: 'projects',          label: 'Projects',             defaultOn: false },
  { value: 'workItems',         label: 'Work items (seeds)',   defaultOn: false },
] as const;

type ResourceType = typeof RESOURCE_TYPES[number]['value'];

// ctsk-055 — bundles may carry a top-level `nest:` block identifying
// them as a Nest bundle (code/slug/name). Echoed back by the server so
// the dry-run preview can label the bundle for the operator.
interface BundleNestBlock {
  slug: string;
  code: string;
  name: string;
  description?: string;
}

// CTSK-317 — a section's dry-run result. `diff` is new + optional: an
// older server (pre-CTSK-317) simply omits the key, so every existing
// call site that only reads inserted/skipped/errors keeps working
// unchanged. `updated` was already section-shaped elsewhere (see
// SummaryTable) but wasn't in this interface pre-CTSK-317 — added here
// too since diff-bearing sections (nest, today) can report it.
interface DryRunSection {
  inserted: number;
  updated?: number;
  skipped: number;
  errors: string[];
  diff?: BundleDiffEntry[];
}

interface ImportSummary {
  dryRun: boolean;
  orgId: string;
  nest?: BundleNestBlock | null;
  summary: {
    // ctsk-056 — `nest` section emitted when the bundle's nest: block
    // upserts into the nests table. Optional in the type so older
    // server responses (pre-ctsk-056) still parse cleanly.
    nest?: DryRunSection;
    containers: DryRunSection;
    tags:       DryRunSection;
    rules:      DryRunSection;
    fileWorkflows: DryRunSection;
    workflows: DryRunSection;
    // Optional in the type so a pre-this-change server response still
    // parses (the summary block predates custom-properties).
    customProperties?: DryRunSection;
    // #128 — projects + work items. Optional for the same forward-
    // compatibility reason.
    projects?: DryRunSection;
    workItems?: DryRunSection;
    // CTSK-303 — tool references (DSR self-registration). Optional
    // for the same forward-compatibility reason.
    tools?: DryRunSection;
  };
  // CTSK-285 — install-time warnings (additive key; older servers omit).
  warnings?: string[];
}

export function BundlesPanel() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* CTSK-286 — panel header with the help link. Mirrors the
          ProjectDetail "Sessions" header pattern (CTSK-281): plain
          anchor, muted text, full-page nav is fine for /help/*. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16 }}>Bundles</h2>
        <a
          href="/help/bundles"
          style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'none' }}
          title="What are bundles?"
        >
          Help ↗
        </a>
      </div>
      <ExportSection />
      <ImportSection />
      {/* CTSK-285 — installed-bundle audit history, below Import/Export. */}
      <InstalledHistorySection />
      <Notes />
    </div>
  );
}

// ─── Export ──────────────────────────────────────────────────────────────

function ExportSection() {
  const [selected, setSelected] = useState<Set<ResourceType>>(
    // Initialize with only default-on resource types. #128 added
    // projects + workItems as default-OFF so the export checkboxes
    // don't auto-include them (bundles would balloon as orgs accrue
    // work items).
    new Set(RESOURCE_TYPES.filter((t) => t.defaultOn).map((t) => t.value)),
  );
  // ctsk-…: optional nest filter. When set, the server uses the
  // tag-prefix scan to restrict the export to that nest's resources,
  // and stamps a top-level `nest:` block on the bundle so the JSON is
  // a drop-in replacement for a hand-assembled seed/nests/*/ bundle.
  const [nestSlug, setNestSlug] = useState<string>('');
  const [busy, setBusy] = useState(false);

  const nestsQ = useQuery({
    queryKey: ['nests'],
    queryFn: () => apiList<Nest>('/nests'),
    staleTime: 60_000,
  });
  const nests = nestsQ.data?.data ?? [];

  function toggle(t: ResourceType) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  async function downloadBundle() {
    if (selected.size === 0) {
      toast.error('Select at least one resource type to export.');
      return;
    }
    setBusy(true);
    try {
      const types = Array.from(selected).join(',');
      const qs = new URLSearchParams({ types });
      if (nestSlug) qs.set('nest', nestSlug);
      const result = await apiSingle<Record<string, unknown>>(
        `/admin/bundle/export?${qs.toString()}`,
      );
      // Trigger browser download.
      const json = JSON.stringify(result, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      // When filtering by nest, include the nest slug in the filename
      // so multiple nest bundles don't collide in a Downloads folder.
      const nestSlugFragment = nestSlug ? `-${nestSlug}` : '';
      a.href = url;
      a.download = `captain-tasko-bundle${nestSlugFragment}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const scope = nestSlug ? ` from ${nestSlug} nest` : '';
      toast.success(`Bundle downloaded${scope} (${selected.size} resource type${selected.size === 1 ? '' : 's'})`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        padding: 20,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 16 }}>⤓ Export bundle</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
        Download this org's config as a JSON file. Use for backups, or to copy
        config into another org via the import below.
      </p>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          marginBottom: 12,
          maxWidth: 360,
        }}
      >
        {RESOURCE_TYPES.map((t) => (
          <label
            key={t.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              borderRadius: 4,
              cursor: 'pointer',
              background: selected.has(t.value) ? 'var(--surface-sunken)' : 'transparent',
            }}
          >
            <input
              type="checkbox"
              checked={selected.has(t.value)}
              onChange={() => toggle(t.value)}
            />
            <span>{t.label}</span>
          </label>
        ))}
      </div>
      {nests.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 12,
            maxWidth: 360,
            fontSize: 13,
          }}
        >
          <label htmlFor="bundle-export-nest" style={{ color: 'var(--text-muted)' }}>
            Filter by nest:
          </label>
          <select
            id="bundle-export-nest"
            value={nestSlug}
            onChange={(e) => setNestSlug(e.target.value)}
            style={{
              padding: '4px 8px',
              borderRadius: 4,
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-sunken)',
              color: 'inherit',
              fontSize: 13,
            }}
          >
            <option value="">All resources (whole org)</option>
            {nests.map((n) => (
              <option key={n.slug} value={n.slug}>
                {n.code} — {n.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {nestSlug && selected.has('workflows') && (
        <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 12px' }}>
          Work-item workflows have no nest membership signal — the bundle will
          include an empty <code>workflows: []</code>. Hand-assemble workflows
          for nest bundles, or export without the nest filter.
        </p>
      )}
      <button
        type="button"
        className="btn btn--primary"
        onClick={downloadBundle}
        disabled={busy || selected.size === 0}
      >
        {busy
          ? 'Downloading…'
          : nestSlug
            ? `Download ${nestSlug} (${selected.size})`
            : `Download (${selected.size})`}
      </button>
    </section>
  );
}

// ─── Import ──────────────────────────────────────────────────────────────

function ImportSection() {
  const qc = useQueryClient();
  const [bundleText, setBundleText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportSummary | null>(null);
  // CTSK-61 — gate Import on a successful dry-run with 0 errors. Reset on
  // any textarea edit so the operator can never Import a bundle they
  // haven't just previewed.
  const [dryRunOk, setDryRunOk] = useState(false);

  // CTSK-61 — memoized parse + client-side Zod validation. Runs on every
  // textarea change and drives the stats panel + tree viewer + Preview
  // button enablement. Cheap enough for interactive typing (bundles are
  // small; JSON.parse + zod .safeParse on the tightest strict-mode section
  // schemas both run in single-digit ms on realistic inputs).
  const parseResult = useMemo(() => parseBundleText(bundleText), [bundleText]);

  function tryParse(): Record<string, unknown> | null {
    setParseError(null);
    if (!bundleText.trim()) {
      setParseError('Paste a bundle JSON above or upload a file.');
      return null;
    }
    try {
      return JSON.parse(bundleText) as Record<string, unknown>;
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Invalid JSON');
      return null;
    }
  }

  const dryRun = useMutation({
    mutationFn: async () => {
      const bundle = tryParse();
      if (!bundle) throw new Error('parse failed');
      // apiSingle unwraps the { data: ... } envelope so `preview.summary`
      // is the actual summary object the route returned. Using apiFetch
      // here was a latent bug from #100 — preview.summary was always
      // undefined because the real summary sat at preview.data.summary.
      // Only surfaced now (#126) on the first real-world import attempt.
      return apiSingle<ImportSummary>('/admin/bundle/import', {
        method: 'POST',
        body: { bundle, dryRun: true },
      });
    },
    onSuccess: (data) => {
      setPreview(data);
      // CTSK-61 — gate: dry-run must produce zero server-side errors before
      // the operator can commit. Any per-section error line flips this back
      // to false; the Import button stays disabled with a "resolve errors
      // first" title tip.
      const errorCount = countSummaryErrors(data.summary);
      setDryRunOk(errorCount === 0);
    },
    onError: (err: Error) => {
      setDryRunOk(false);
      if (err.message !== 'parse failed') {
        toast.error(err.message);
      }
    },
  });

  const apply = useMutation({
    mutationFn: async () => {
      const bundle = tryParse();
      if (!bundle) throw new Error('parse failed');
      // #131 — bundle imports have occasionally hung indefinitely after
      // the server side completed (DB rows landed, response never made
      // it to the client). We don't have a repro to chase the root
      // cause, so this layer adds a 90-second hard timeout via
      // AbortSignal — well past any legitimate import (~1-2s in dev)
      // but short enough that a stuck request surfaces as a clear
      // error toast instead of a grey button forever.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90_000);
      try {
        return await apiSingle<ImportSummary>('/admin/bundle/import', {
          method: 'POST',
          body: { bundle, dryRun: false },
          signal: controller.signal,
        });
      } catch (err) {
        // Differentiate the timeout case so the toast is actionable
        // (the import probably already succeeded server-side, just
        // refresh to see it). Without this the user would only see
        // a cryptic AbortError.
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw new Error(
            'Import timed out (the server may have already finished). Reload the page to see the latest state.',
          );
        }
        throw err;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    onSettled: () => {
      // #131 — invalidate page-state queries on both success AND error.
      // If a partial / hanging response lands but onSuccess doesn't
      // run cleanly, the listed resources still need a refetch so the
      // user's view catches up to whatever actually landed on the
      // server. Cheap to over-invalidate; expensive to leave the
      // user staring at stale data.
      qc.invalidateQueries({ queryKey: ['storage-containers'] });
      qc.invalidateQueries({ queryKey: ['tags'] });
      qc.invalidateQueries({ queryKey: ['tagging-rules'] });
      qc.invalidateQueries({ queryKey: ['file-workflows'] });
      qc.invalidateQueries({ queryKey: ['workflows'] });
      qc.invalidateQueries({ queryKey: ['nests'] });
      qc.invalidateQueries({ queryKey: ['custom-properties'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['workitems'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      // CTSK-285 — a committed import lands a new history event.
      qc.invalidateQueries({ queryKey: ['bundles-installed'] });
    },
    onSuccess: (data) => {
      // Defensive: an older server response (pre-#119) wouldn't include
      // workflows. Use ?? 0 fallbacks so a missing section can't crash
      // the success handler and leave the button stuck on "Importing…".
      try {
        const s = data?.summary;
        const total =
          (s?.containers?.inserted ?? 0) +
          (s?.tags?.inserted ?? 0) +
          (s?.rules?.inserted ?? 0) +
          (s?.fileWorkflows?.inserted ?? 0) +
          (s?.workflows?.inserted ?? 0) +
          (s?.nest?.inserted ?? 0);
        toast.success(`Bundle imported: ${total} new row${total === 1 ? '' : 's'}`);
      } catch (err) {
        // Never let success-handler counting kill the UX.
        toast.success('Bundle imported.');
        console.warn('Bundle import success summary unreadable:', err, data);
      }
      setBundleText('');
      setPreview(null);
      setDryRunOk(false);
    },
    onError: (err: Error) => {
      if (err.message !== 'parse failed') {
        toast.error(err.message);
      }
    },
  });

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setBundleText(text);
    setPreview(null);
    setParseError(null);
    setDryRunOk(false);
    e.target.value = ''; // allow re-uploading the same file
  }

  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        padding: 20,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 16 }}>⤒ Import bundle</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
        Load containers / tags / rules / workflows into the <strong>active org</strong>.
        Idempotent on name — existing rows are skipped, so re-running is safe.
      </p>

      <div style={{ marginBottom: 8 }}>
        <label
          className="btn"
          style={{ display: 'inline-block', cursor: 'pointer' }}
        >
          📂 Choose file…
          <input
            type="file"
            accept="application/json,.json"
            onChange={onFile}
            style={{ display: 'none' }}
          />
        </label>
        <span style={{ marginLeft: 12, color: 'var(--text-muted)', fontSize: 12 }}>
          or paste below
        </span>
      </div>

      <textarea
        value={bundleText}
        onChange={(e) => { setBundleText(e.target.value); setPreview(null); setParseError(null); setDryRunOk(false); }}
        placeholder='Paste bundle JSON here…&#10;{ "containers": [...], "tags": [...], "rules": [...], "fileWorkflows": [...], "workflows": [...] }'
        rows={10}
        style={{
          width: '100%',
          padding: 10,
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          border: '1px solid var(--border-strong)',
          borderRadius: 4,
          background: 'var(--surface-sunken)',
          color: 'var(--text-primary)',
        }}
      />

      {parseError && (
        <div
          style={{
            color: 'var(--danger-primary, #B3261E)',
            fontSize: 12,
            marginTop: 6,
          }}
        >
          ✗ {parseError}
        </div>
      )}

      {/* CTSK-61 — live stats panel + client-side shape check. Renders
         only when the pasted text parses as JSON. Even if the Zod
         BundleV2Schema flags issues we still show what we can; the
         validation state is a chip on the panel, not a gate. */}
      {parseResult.ok && (
        <BundleStatsPanel
          bundle={parseResult.bundle}
          byteSize={parseResult.byteSize}
          zodOk={parseResult.zodOk}
          zodError={parseResult.zodError}
        />
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <button
          type="button"
          className="btn"
          onClick={() => dryRun.mutate()}
          disabled={dryRun.isPending || apply.isPending || !bundleText.trim() || !parseResult.ok}
          title={
            !bundleText.trim()
              ? 'Paste a bundle first'
              : !parseResult.ok
                ? `Fix JSON syntax first — ${parseResult.error}`
                : 'Run a server-side dry-run without committing'
          }
        >
          {dryRun.isPending ? 'Previewing…' : 'Preview (dry run)'}
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => apply.mutate()}
          disabled={dryRun.isPending || apply.isPending || !bundleText.trim() || !dryRunOk}
          title={
            !dryRunOk
              ? 'Run a clean Preview first (Import stays disabled until dry-run returns zero errors)'
              : 'Commit the bundle'
          }
        >
          {apply.isPending ? 'Importing…' : dryRunOk ? 'Import ✓' : 'Import (dry-run required)'}
        </button>
      </div>

      {preview && preview.nest && <NestBadge nest={preview.nest} />}
      {preview && <SummaryTable summary={preview.summary} dryRun={preview.dryRun} />}
      {/* CTSK-317 — collapsible diff-tree, below the counts table (non-
         goal: don't restyle the counts header above). Renders nothing
         when every section's `diff` is absent — legacy server response,
         the SummaryTable above already IS the counts-only fallback. */}
      {preview && <BundleDiffTree summary={preview.summary} />}

      {/* CTSK-61 — collapsible + syntax-highlighted tree viewer. Rendered
         under the summary so the operator's eye lands on the counts
         first, then can drill into raw JSON per section on demand. */}
      {parseResult.ok && <BundleTreeViewer bundle={parseResult.bundle} />}
    </section>
  );
}

// ─── CTSK-61 helpers ─────────────────────────────────────────────────
//
// parseBundleText, countSummaryErrors, computeBundleStats,
// BundleStatsPanel, BundleTreeViewer.
//
// All pure / presentational — no external side effects, no network,
// no persisted state. The heavy lifting (real cross-ref resolution,
// name uniqueness, org-scoped conflict detection) stays server-side
// under the dry-run route, which the operator hits via the Preview
// button. These helpers exist to make the pre-Preview inspection
// pass richer so the operator has "does this bundle look right?"
// signal before spending a round-trip.
// ─────────────────────────────────────────────────────────────────────

type ParsedBundle = {
  ok: true;
  bundle: Record<string, unknown>;
  byteSize: number;
  zodOk: boolean;
  zodError?: string;
} | {
  ok: false;
  error: string;
};

function parseBundleText(text: string): ParsedBundle {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, error: 'empty' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Invalid JSON' };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, error: 'Bundle must be a JSON object at the top level' };
  }
  const bundle = parsed as Record<string, unknown>;
  const byteSize = new Blob([text]).size;
  // Zod check is a soft signal — a bundle can fail the strict v2 sub-schemas
  // and still be a valid v1 bundle the importer will accept. We report the
  // Zod result as a chip, not a gate.
  const zod = BundleV2Schema.safeParse(bundle);
  return {
    ok: true,
    bundle,
    byteSize,
    zodOk: zod.success,
    zodError: zod.success ? undefined : zod.error.issues[0]?.message ?? 'Schema mismatch',
  };
}

function countSummaryErrors(summary: ImportSummary['summary']): number {
  let n = 0;
  for (const section of [
    summary.nest, summary.containers, summary.tags, summary.rules,
    summary.fileWorkflows, summary.workflows, summary.customProperties,
    summary.projects, summary.workItems, summary.tools,
  ]) {
    if (section) n += section.errors.length;
  }
  return n;
}

interface BundleStats {
  bytesFormatted: string;
  sections: Array<{ key: string; count: number }>;
  tagsWithColor: number;
  tagsTotal: number;
  rulesResolved: number;
  rulesTotal: number;
  topPriorityRule: { name: string; priority: number } | null;
}

function computeBundleStats(bundle: Record<string, unknown>, byteSize: number): BundleStats {
  const kb = byteSize / 1024;
  const bytesFormatted =
    byteSize < 1024 ? `${byteSize} B` : `${kb.toFixed(kb < 10 ? 2 : 1)} KB`;

  // Section counts — mirror the RESOURCE_TYPES layout (v1) + v2 additions.
  const sectionKeys = [
    'nest', 'containers', 'tags', 'rules', 'fileWorkflows', 'workflows',
    'customProperties', 'projects', 'workItems', 'tools',
  ] as const;
  const sections: Array<{ key: string; count: number }> = [];
  for (const key of sectionKeys) {
    const v = bundle[key];
    if (v == null) continue;
    if (Array.isArray(v)) sections.push({ key, count: v.length });
    else if (typeof v === 'object') sections.push({ key, count: 1 }); // e.g. nest: {...}
  }

  // Tag color coverage.
  const tags = Array.isArray(bundle.tags) ? (bundle.tags as Array<Record<string, unknown>>) : [];
  const tagsWithColor = tags.filter((t) => typeof t?.color === 'string' && t.color).length;

  // Rule cross-ref resolution rate (bundle-local only — a bare-name ref that
  // resolves against an existing DB row shows here as "unresolved" but will
  // succeed server-side; this stat is a heuristic, not authoritative).
  const rules = Array.isArray(bundle.rules) ? (bundle.rules as Array<Record<string, unknown>>) : [];
  const containers = Array.isArray(bundle.containers) ? (bundle.containers as Array<Record<string, unknown>>) : [];
  const tagNames = new Set(tags.map((t) => typeof t?.name === 'string' ? t.name : '').filter(Boolean));
  const containerNames = new Set(containers.map((c) => typeof c?.name === 'string' ? c.name : '').filter(Boolean));
  let rulesResolved = 0;
  for (const r of rules) {
    const tagRef = Array.isArray(r?.tagsToApply) ? (r.tagsToApply as unknown[]) : [];
    const containerRef = typeof r?.containerToAssign === 'string' ? r.containerToAssign : null;
    const allTagsResolve = tagRef.every((t) => typeof t === 'string' && tagNames.has(t));
    const containerResolves = containerRef ? containerNames.has(containerRef) : true;
    if (allTagsResolve && containerResolves) rulesResolved += 1;
  }

  // Highest-priority rule preview.
  let topPriorityRule: BundleStats['topPriorityRule'] = null;
  for (const r of rules) {
    const priority = typeof r?.priority === 'number' ? r.priority : 0;
    const name = typeof r?.name === 'string' ? r.name : '(unnamed)';
    if (!topPriorityRule || priority > topPriorityRule.priority) {
      topPriorityRule = { name, priority };
    }
  }

  return {
    bytesFormatted,
    sections,
    tagsWithColor,
    tagsTotal: tags.length,
    rulesResolved,
    rulesTotal: rules.length,
    topPriorityRule,
  };
}

const SECTION_LABEL: Record<string, string> = {
  nest: 'Nest identity',
  containers: 'Containers',
  tags: 'Tags',
  rules: 'Rules',
  fileWorkflows: 'File workflows',
  workflows: 'Work-item workflows',
  customProperties: 'Custom properties',
  projects: 'Projects',
  workItems: 'Work items',
  // CTSK-303 — references to installed tools (DSR self-registration).
  tools: 'Tool references',
};

function BundleStatsPanel({
  bundle,
  byteSize,
  zodOk,
  zodError,
}: {
  bundle: Record<string, unknown>;
  byteSize: number;
  zodOk: boolean;
  zodError?: string;
}) {
  const stats = useMemo(() => computeBundleStats(bundle, byteSize), [bundle, byteSize]);
  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        borderRadius: 6,
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-sunken)',
        fontSize: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>📊 Bundle stats</strong>
        <span style={{ color: 'var(--text-muted)' }}>· {stats.bytesFormatted}</span>
        {zodOk ? (
          <span
            title="BundleV2Schema.safeParse passed on the client (server dry-run is still authoritative)."
            style={{
              padding: '1px 8px',
              borderRadius: 10,
              background: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)',
              color: 'var(--accent-primary)',
              fontSize: 11,
            }}
          >
            shape ✓
          </span>
        ) : (
          <span
            title={`Client Zod check flagged: ${zodError ?? 'schema mismatch'}. This is a heuristic — the server dry-run is still authoritative and a v1 bundle can validly fail v2's strict sub-schemas.`}
            style={{
              padding: '1px 8px',
              borderRadius: 10,
              background: 'color-mix(in srgb, var(--warning-primary, #B78100) 15%, transparent)',
              color: 'var(--warning-primary, #B78100)',
              fontSize: 11,
            }}
          >
            shape ⚠
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
        {stats.sections.map((s) => (
          <span key={s.key} style={{ fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ color: 'var(--text-muted)' }}>{SECTION_LABEL[s.key] ?? s.key}:</span>{' '}
            <strong>{s.count}</strong>
          </span>
        ))}
      </div>
      {(stats.tagsTotal > 0 || stats.rulesTotal > 0) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px 14px',
            marginTop: 6,
            color: 'var(--text-muted)',
            fontSize: 11,
          }}
        >
          {stats.tagsTotal > 0 && (
            <span title="Tags with an explicit color attribute — the rest will render with the default color at import.">
              tag colors: {stats.tagsWithColor} / {stats.tagsTotal} (
              {Math.round((stats.tagsWithColor / stats.tagsTotal) * 100)}%)
            </span>
          )}
          {stats.rulesTotal > 0 && (
            <span title="Bundle-local ref resolution. Bare-name refs that resolve against pre-existing org rows are counted here as unresolved even though the server will resolve them at import.">
              rule refs (bundle-local): {stats.rulesResolved} / {stats.rulesTotal} (
              {Math.round((stats.rulesResolved / stats.rulesTotal) * 100)}%)
            </span>
          )}
          {stats.topPriorityRule && (
            <span title="Highest-priority rule in the bundle. Priority ties are broken by first-in-array order at import.">
              top-priority: <code>{stats.topPriorityRule.name}</code> (priority {stats.topPriorityRule.priority})
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function BundleTreeViewer({ bundle }: { bundle: Record<string, unknown> }) {
  // Render each top-level key as a collapsible <details>. For simple
  // scalars (nest) render one block per key; for arrays render a header
  // with item count + the pretty-printed JSON body. Uses <details>
  // instead of a custom disclosure component so keyboard nav (Space /
  // Enter to toggle) works for free.
  const keys = Object.keys(bundle);
  if (keys.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>🌲 Formatted bundle</div>
      {keys.map((key) => (
        <BundleSection key={key} name={key} value={bundle[key]} />
      ))}
    </div>
  );
}

function BundleSection({ name, value }: { name: string; value: unknown }) {
  const count = Array.isArray(value) ? value.length : value == null ? 0 : 1;
  const label = SECTION_LABEL[name] ?? name;
  // Pretty-print + syntax-highlight only when the section is expanded.
  // Not memoizing across all sections keeps peak memory low on large
  // bundles; hljs.highlight on a section is O(section size) and only
  // runs when the operator actually opens it.
  return (
    <details style={{ marginBottom: 6 }}>
      <summary
        style={{
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: 4,
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-subtle)',
          fontSize: 12,
          fontFamily: 'var(--font-mono)',
          userSelect: 'none',
        }}
      >
        <strong>{label}</strong>{' '}
        <span style={{ color: 'var(--text-muted)' }}>
          ({Array.isArray(value) ? `${count} items` : 'object'})
        </span>
      </summary>
      <BundleSectionBody value={value} />
    </details>
  );
}

function BundleSectionBody({ value }: { value: unknown }) {
  const pretty = useMemo(() => JSON.stringify(value, null, 2), [value]);
  const highlighted = useMemo(() => {
    try {
      return hljs.highlight(pretty, { language: 'json' }).value;
    } catch {
      // hljs should never throw on valid JSON, but never let the viewer
      // component crash the whole panel just because a stringify quirk
      // slipped through.
      return escapeHtml(pretty);
    }
  }, [pretty]);
  return (
    <pre
      style={{
        margin: '4px 0 0 12px',
        padding: 10,
        background: 'var(--surface-sunken)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 4,
        fontSize: 11,
        fontFamily: 'var(--font-mono)',
        maxHeight: 320,
        overflow: 'auto',
      }}
    >
      <code
        className="hljs language-json"
        // hljs.highlight returns HTML with safe span tags; no user-authored
        // markup enters this string (input is JSON.stringify output).
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    </pre>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ctsk-055 — Visual badge that flags a bundle as a Nest bundle.
// Renders between the action buttons and the summary table so the
// operator sees "this is the WG nest" before scanning the row counts.
function NestBadge({ nest }: { nest: BundleNestBlock }) {
  return (
    <div
      style={{
        marginTop: 16,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        borderRadius: 8,
        border: '1px solid var(--accent-primary)',
        background: 'color-mix(in srgb, var(--accent-primary) 10%, var(--surface-raised))',
        fontSize: 13,
      }}
      title={nest.description ?? `Nest bundle: ${nest.name}`}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          color: '#fff',
          background: 'var(--accent-primary)',
          padding: '3px 6px',
          borderRadius: 4,
          letterSpacing: '.04em',
        }}
      >
        {nest.code}
      </span>
      <span>
        <strong>Nest bundle: {nest.name}</strong>
        <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontFamily: 'var(--font-mono)' }}>
          nest/{nest.slug}
        </span>
      </span>
    </div>
  );
}

function SummaryTable({
  summary,
  dryRun,
}: {
  summary: ImportSummary['summary'];
  dryRun: boolean;
}) {
  const rows: Array<{ label: string; ins: number; skip: number; errs: string[] }> = [
    // ctsk-056 — nest row only shown when the server emitted it (bundle
    // carried a nest: block). Older responses are silent here.
    ...(summary.nest ? [{ label: 'Nest identity', ins: summary.nest.inserted, skip: summary.nest.skipped, errs: summary.nest.errors }] : []),
    { label: 'Storage containers', ins: summary.containers.inserted,    skip: summary.containers.skipped,    errs: summary.containers.errors },
    { label: 'Tags',               ins: summary.tags.inserted,          skip: summary.tags.skipped,          errs: summary.tags.errors },
    { label: 'Tagging rules',      ins: summary.rules.inserted,         skip: summary.rules.skipped,         errs: summary.rules.errors },
    { label: 'File workflows',     ins: summary.fileWorkflows.inserted, skip: summary.fileWorkflows.skipped, errs: summary.fileWorkflows.errors },
    { label: 'Work-item workflows',ins: summary.workflows.inserted,     skip: summary.workflows.skipped,     errs: summary.workflows.errors },
    // Custom properties row only when the server emitted the section.
    // Older server responses (pre-Batch B) won't have it.
    ...(summary.customProperties
      ? [{
          label: 'Custom properties',
          ins: summary.customProperties.inserted,
          skip: summary.customProperties.skipped,
          errs: summary.customProperties.errors,
        }]
      : []),
    // #128 — projects + work items. Same forward-compat pattern.
    ...(summary.projects
      ? [{
          label: 'Projects',
          ins: summary.projects.inserted,
          skip: summary.projects.skipped,
          errs: summary.projects.errors,
        }]
      : []),
    ...(summary.workItems
      ? [{
          label: 'Work items',
          ins: summary.workItems.inserted,
          skip: summary.workItems.skipped,
          errs: summary.workItems.errors,
        }]
      : []),
    // CTSK-303 — tool references. Same forward-compat pattern.
    ...(summary.tools
      ? [{
          label: 'Tool references',
          ins: summary.tools.inserted,
          skip: summary.tools.skipped,
          errs: summary.tools.errors,
        }]
      : []),
  ];
  const totalIns = rows.reduce((a, r) => a + r.ins, 0);
  const totalSkip = rows.reduce((a, r) => a + r.skip, 0);
  const totalErrs = rows.reduce((a, r) => a + r.errs.length, 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: dryRun ? 'var(--accent-primary)' : 'var(--text-primary)',
          marginBottom: 6,
        }}
      >
        {dryRun ? '🔍 Preview (nothing committed)' : '✓ Imported'} —
        {' '}{totalIns} new · {totalSkip} skipped · {totalErrs} error{totalErrs === 1 ? '' : 's'}
      </div>
      <table className="grid">
        <thead>
          <tr>
            <th>Resource</th>
            <th style={{ width: 80, textAlign: 'right' }}>New</th>
            <th style={{ width: 80, textAlign: 'right' }}>Skipped</th>
            <th>Errors</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td>{r.label}</td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {r.ins > 0 ? <strong style={{ color: 'var(--accent-primary)' }}>+{r.ins}</strong> : '—'}
              </td>
              <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {r.skip > 0 ? r.skip : '—'}
              </td>
              <td style={{ color: 'var(--danger-primary, #B3261E)', fontSize: 12 }}>
                {r.errs.length > 0 ? r.errs.map((e, i) => <div key={i}>{e}</div>) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── CTSK-317 — Dry-run diff-tree preview ────────────────────────────────
//
// Renders BELOW the existing SummaryTable (non-goal: restyling the
// counts headers). One collapsible <details> per section that carries a
// `diff[]`; a section with no `diff` (older server, or a section this
// ticket doesn't populate — see BundleDiffEntry's shared-schema note)
// is silently skipped, so a legacy dry-run response renders NOTHING
// here and the SummaryTable above is the whole story — the backward-
// compat "counts-only fallback" the AC calls for.

/** Sections in the same display order as SummaryTable/HISTORY_SECTIONS,
 *  restricted to the keys DryRunSection can carry (nest is handled
 *  specially since it's singular, not part of the array below). */
const DIFF_SECTION_ORDER: Array<keyof ImportSummary['summary']> = [
  'nest', 'containers', 'tags', 'rules', 'fileWorkflows', 'workflows',
  'customProperties', 'projects', 'workItems', 'tools',
];

function BundleDiffTree({ summary }: { summary: ImportSummary['summary'] }) {
  const sections = DIFF_SECTION_ORDER
    .map((key) => ({ key, result: summary[key] }))
    .filter((s): s is { key: keyof ImportSummary['summary']; result: DryRunSection } =>
      Array.isArray(s.result?.diff),
    );
  if (sections.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        🔬 Diff preview — what will change
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sections.map(({ key, result }) => (
          <BundleDiffSection key={key} sectionKey={key} result={result} />
        ))}
      </div>
    </div>
  );
}

const DIFF_ACTION_META: Record<
  BundleDiffEntry['action'],
  { icon: string; color: string; label: string }
> = {
  insert: { icon: '+', color: 'var(--accent-primary)', label: 'insert' },
  update: { icon: '~', color: 'var(--warning-primary, #B78100)', label: 'update' },
  skip:   { icon: '−', color: 'var(--text-muted)', label: 'skip' },
  error:  { icon: '✗', color: 'var(--danger-primary, #B3261E)', label: 'error' },
};

function BundleDiffSection({
  sectionKey,
  result,
}: {
  sectionKey: keyof ImportSummary['summary'];
  result: DryRunSection;
}) {
  const diff = result.diff ?? [];
  // Default-open sections with anything the operator should look
  // twice at (an update changes existing data; an error blocks that
  // row); default-collapsed for all-insert/all-skip sections — those
  // read fine from the counts row alone.
  const hasUpdatesOrErrors = diff.some((d) => d.action === 'update' || d.action === 'error');
  return (
    <details
      open={hasUpdatesOrErrors}
      style={{
        border: '1px solid var(--border-subtle)',
        borderRadius: 6,
        background: 'var(--surface-sunken)',
      }}
    >
      <summary
        style={{
          cursor: 'pointer',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
          userSelect: 'none',
          listStyle: 'none',
        }}
      >
        <span aria-hidden="true" style={{ color: 'var(--text-muted)', fontSize: 11 }}>▸</span>
        <strong>{SECTION_LABEL[sectionKey] ?? sectionKey}</strong>
        <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
          {result.inserted > 0 && <span style={{ color: 'var(--accent-primary)' }}>+{result.inserted} </span>}
          {(result.updated ?? 0) > 0 && <span style={{ color: 'var(--warning-primary, #B78100)' }}>~{result.updated} </span>}
          {result.skipped > 0 && <span>{result.skipped} skipped </span>}
          {result.errors.length > 0 && <span style={{ color: 'var(--danger-primary, #B3261E)' }}>{result.errors.length} error{result.errors.length === 1 ? '' : 's'}</span>}
        </span>
      </summary>
      <div style={{ padding: '4px 12px 10px 28px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {diff.map((entry, i) => (
          <BundleDiffRow key={`${entry.action}-${entry.name}-${i}`} entry={entry} />
        ))}
      </div>
    </details>
  );
}

function BundleDiffRow({ entry }: { entry: BundleDiffEntry }) {
  const meta = DIFF_ACTION_META[entry.action];
  return (
    <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          aria-label={meta.label}
          title={meta.label}
          style={{ color: meta.color, fontWeight: 700, fontFamily: 'var(--font-mono)', width: 14, textAlign: 'center' }}
        >
          {meta.icon}
        </span>
        <span style={{ fontFamily: 'var(--font-mono)' }}>{entry.name}</span>
        {entry.action === 'insert' && <DiffInsertPreview value={entry.after} />}
      </div>
      {entry.action === 'update' && (
        <div style={{ marginLeft: 20 }}>
          <DiffFieldChanges before={entry.before} after={entry.after} />
        </div>
      )}
      {(entry.action === 'skip' || entry.action === 'error') && entry.reason && (
        <div style={{ marginLeft: 20, color: meta.color }}>{entry.reason}</div>
      )}
    </div>
  );
}

/** For an insert row: show the first 2-3 operator-relevant fields
 *  inline, so the row reads as more than just a bare name. Skips
 *  `name` itself (already shown) and any nested object/array values
 *  (those belong in the full JSON tree viewer below, not a one-line
 *  preview). */
function DiffInsertPreview({ value }: { value: unknown }) {
  if (typeof value !== 'object' || value === null) return null;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([k, v]) => k !== 'name' && v != null && typeof v !== 'object')
    .slice(0, 3);
  if (entries.length === 0) return null;
  return (
    <span style={{ color: 'var(--text-muted)' }}>
      {entries.map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}
    </span>
  );
}

/** For an update row: diff `before` vs `after` field-by-field, showing
 *  ONLY the fields that actually changed (unchanged fields are noise —
 *  the operator already knows the row exists). */
function DiffFieldChanges({ before, after }: { before: unknown; after: unknown }) {
  const b = (before && typeof before === 'object' ? before : {}) as Record<string, unknown>;
  const a = (after && typeof after === 'object' ? after : {}) as Record<string, unknown>;
  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)])).filter(
    (k) => JSON.stringify(b[k]) !== JSON.stringify(a[k]),
  );
  if (keys.length === 0) {
    return <span style={{ color: 'var(--text-muted)' }}>(no field-level changes detected)</span>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {keys.map((k) => (
        <div key={k}>
          <span style={{ color: 'var(--text-muted)' }}>{k}:</span>{' '}
          <span style={{ color: 'var(--danger-primary, #B3261E)', textDecoration: 'line-through' }}>
            {b[k] === undefined ? '(unset)' : String(b[k])}
          </span>
          {' → '}
          <span style={{ color: 'var(--accent-primary)' }}>
            {a[k] === undefined ? '(unset)' : String(a[k])}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── CTSK-285 — Installed History ────────────────────────────────────────
//
// Chronological (newest-first) record of every applied bundle import in
// the active org, sourced from GET /admin/bundles/installed (the
// `admin.bundle_import` audit rows). One collapsible row per event:
// collapsed shows bundle label + uid + relative install time + actor +
// section counts; expanded shows the full section breakdown (item
// names when captured), import warnings and raw bundle metadata.
// Events recorded before 0.1.122 carry counts only — those rows say so
// instead of pretending the details never existed.

/** Sections in display order + the native admin surface each links to
 *  (section-level: per-item deep links don't exist for these types). */
const HISTORY_SECTIONS: Array<{ key: string; href: string | null }> = [
  { key: 'nest', href: null },
  { key: 'containers', href: '/storage' },
  { key: 'tags', href: '/tags' },
  { key: 'rules', href: '/tags/rules' },
  { key: 'fileWorkflows', href: '/file-workflows' },
  { key: 'workflows', href: null },
  { key: 'customProperties', href: '/custom-properties' },
  { key: 'projects', href: '/projects' },
  { key: 'workItems', href: '/workitems' },
  { key: 'tools', href: null },
];

function fmtRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 60) return `${d} days ago`;
  return new Date(iso).toISOString().slice(0, 10);
}

/** Compact "3 tags · 2 rules · 5 CPs" line from an event's sections. */
function sectionCountsLine(ev: BundleInstallEvent): string {
  const SHORT: Record<string, [string, string]> = {
    nest: ['nest', 'nests'],
    containers: ['container', 'containers'],
    tags: ['tag', 'tags'],
    rules: ['rule', 'rules'],
    fileWorkflows: ['file workflow', 'file workflows'],
    workflows: ['workflow', 'workflows'],
    customProperties: ['CP', 'CPs'],
    projects: ['project', 'projects'],
    workItems: ['work item', 'work items'],
    tools: ['tool', 'tools'],
  };
  const parts: string[] = [];
  for (const { key } of HISTORY_SECTIONS) {
    const s = (ev.sections as Record<string, { inserted: number; updated: number; skipped: number } | undefined>)[key];
    if (!s) continue;
    const n = s.inserted + s.updated + s.skipped;
    if (n === 0) continue;
    const [one, many] = SHORT[key] ?? [key, key];
    parts.push(`${n} ${n === 1 ? one : many}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'no sections';
}

// ─── CTSK-318 — filter chips + search over the Installed history ────────
//
// All client-side over the already-fetched list (GET /admin/bundles/
// installed returns the whole org history per CTSK-285) — no server
// round-trip, no cap-gating. Filters AND-compose: search ∧ actor ∧
// date-range ∧ has-warnings. State lives in InstalledHistorySection and
// resets on unmount (saving presets is an explicit non-goal).

type HistoryDateRange = '24h' | '7d' | '30d' | 'all';

const HISTORY_DATE_RANGES: Array<{ value: HistoryDateRange; label: string; ms: number | null }> = [
  { value: '24h', label: 'Last 24h', ms: 24 * 60 * 60 * 1000 },
  { value: '7d',  label: 'Last 7d',  ms: 7 * 24 * 60 * 60 * 1000 },
  { value: '30d', label: 'Last 30d', ms: 30 * 24 * 60 * 60 * 1000 },
  { value: 'all', label: 'All',      ms: null },
];

/** The actor string a row DISPLAYS (name ?? email ?? id ?? 'unknown') —
 *  reused as the search target and the dropdown's identity key so what
 *  the operator sees is exactly what they filter on. */
function historyActorLabel(ev: BundleInstallEvent): string {
  return ev.installedBy?.name ?? ev.installedBy?.email ?? ev.installedBy?.id ?? 'unknown';
}

interface HistoryFilterState {
  search: string;
  actor: string;        // '' = all actors
  dateRange: HistoryDateRange;
  warningsOnly: boolean;
}

const HISTORY_FILTERS_CLEARED: HistoryFilterState = {
  search: '',
  actor: '',
  dateRange: 'all',
  warningsOnly: false,
};

function historyFiltersActive(f: HistoryFilterState): boolean {
  return f.search.trim() !== '' || f.actor !== '' || f.dateRange !== 'all' || f.warningsOnly;
}

/** Pure AND-composed predicate pass. `now` injected for testability. */
function filterHistoryEvents(
  events: BundleInstallEvent[],
  f: HistoryFilterState,
  now: number,
): BundleInstallEvent[] {
  if (!historyFiltersActive(f)) return events;
  const needle = f.search.trim().toLowerCase();
  const rangeMs = HISTORY_DATE_RANGES.find((r) => r.value === f.dateRange)?.ms ?? null;
  const cutoff = rangeMs === null ? null : now - rangeMs;
  return events.filter((ev) => {
    if (needle) {
      const hit =
        (ev.bundleName ?? '').toLowerCase().includes(needle) ||
        (ev.bundleUid ?? '').toLowerCase().includes(needle) ||
        historyActorLabel(ev).toLowerCase().includes(needle);
      if (!hit) return false;
    }
    if (f.actor !== '' && historyActorLabel(ev) !== f.actor) return false;
    if (cutoff !== null) {
      const t = new Date(ev.installedAt).getTime();
      // Unparseable timestamps only ever survive under "All" — a bounded
      // range can't honestly claim they're inside the window.
      if (!Number.isFinite(t) || t < cutoff) return false;
    }
    if (f.warningsOnly && ev.warnings.length === 0) return false;
    return true;
  });
}

/** Chip visual — mirrors NestFilterChip's chipStyle (ctsk-056), the
 *  house pattern the ticket points at. Kept local: NestFilterChip
 *  doesn't export its style fn and importing a whole component for a
 *  CSSProperties helper isn't worth the coupling. */
function historyChipStyle(active: boolean): React.CSSProperties {
  return {
    padding: '3px 8px',
    fontSize: 11,
    fontFamily: 'var(--font-mono)',
    fontWeight: active ? 700 : 500,
    borderRadius: 4,
    letterSpacing: '.04em',
    border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-strong)'}`,
    background: active ? 'var(--accent-primary)' : 'var(--surface-raised)',
    color: active ? '#fff' : 'var(--text-primary)',
    cursor: 'pointer',
  };
}

function InstalledHistorySection() {
  const historyQ = useQuery({
    queryKey: ['bundles-installed'],
    queryFn: () => apiList<BundleInstallEvent>('/admin/bundles/installed'),
    // Tab-focus refetch keeps the list current after an import from
    // another window; the apply-mutation invalidation covers this one.
    refetchOnWindowFocus: true,
  });
  const events = historyQ.data?.data ?? [];

  // CTSK-318 — filter/search state over the fetched list.
  const [filters, setFilters] = useState<HistoryFilterState>(HISTORY_FILTERS_CLEARED);
  const filtersActive = historyFiltersActive(filters);

  // Unique installer labels for the actor dropdown — derived from the
  // CURRENT data set (usually 2-5 entries), sorted for stable option
  // order across refetches.
  const actorOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const ev of events) seen.add(historyActorLabel(ev));
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [events]);

  const filteredEvents = useMemo(
    () => filterHistoryEvents(events, filters, Date.now()),
    [events, filters],
  );

  // CTSK-285 UI follow-up (2026-08-06): section is now an accordion
  // that starts collapsed. With many bundle imports the ever-visible
  // list dominated the Bundles page; wrapping it in <details> keeps
  // Import/Export as the primary surface and puts audit history one
  // click away. Open-by-default only when 1-3 events (still glanceable);
  // 0 events also stays collapsed since the empty state is boilerplate.
  const totalCount = events.length;
  const openByDefault = totalCount > 0 && totalCount <= 3;

  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        padding: 20,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <details open={openByDefault}>
        <summary
          style={{
            cursor: 'pointer',
            listStyle: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            userSelect: 'none',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span aria-hidden="true" style={{ color: 'var(--text-muted)', fontSize: 12 }}>▸</span>
            <h2 style={{ margin: 0, fontSize: 16 }}>🗂 Installed history</h2>
            {historyQ.isSuccess && (
              <span
                aria-label={`${totalCount} bundle imports recorded`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 24,
                  height: 20,
                  padding: '0 6px',
                  borderRadius: 10,
                  background: 'color-mix(in srgb, var(--accent-primary) 18%, var(--surface-sunken))',
                  color: 'var(--accent-primary)',
                  fontSize: 11,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {totalCount}
              </span>
            )}
          </span>
          <button
            type="button"
            className="btn"
            onClick={(e) => {
              // Refresh button lives inside <summary> so it stays visible
              // whether the accordion is open or closed. Stop propagation
              // so clicking it doesn't toggle the accordion.
              e.stopPropagation();
              void historyQ.refetch();
            }}
            disabled={historyQ.isFetching}
            title="Re-read the install history"
          >
            {historyQ.isFetching ? 'Refreshing…' : '⟳ Refresh'}
          </button>
        </summary>

        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>
          Every applied bundle import in this org, newest first — which bundle
          landed what, when, and by whom. Dry-run previews are not recorded.
        </p>

        {/* CTSK-318 — filter bar. Only rendered once there is a list to
            filter; loading/error/empty states below stay untouched. */}
        {events.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px 14px',
              flexWrap: 'wrap',
              marginBottom: 10,
              fontSize: 12,
            }}
          >
            <input
              type="search"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search name / uid / installer…"
              aria-label="Search installed bundles"
              style={{
                padding: '4px 8px',
                borderRadius: 4,
                border: '1px solid var(--border-strong)',
                background: 'var(--surface-sunken)',
                color: 'var(--text-primary)',
                fontSize: 12,
                width: 210,
              }}
            />
            <select
              value={filters.actor}
              onChange={(e) => setFilters((f) => ({ ...f, actor: e.target.value }))}
              aria-label="Filter by installer"
              style={{
                padding: '4px 8px',
                borderRadius: 4,
                border: '1px solid var(--border-subtle)',
                background: 'var(--surface-sunken)',
                color: 'inherit',
                fontSize: 12,
              }}
            >
              <option value="">All installers</option>
              {actorOptions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {HISTORY_DATE_RANGES.map((r) => {
                const active = filters.dateRange === r.value;
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setFilters((f) => ({ ...f, dateRange: r.value }))}
                    aria-pressed={active}
                    style={historyChipStyle(active)}
                    title={r.ms === null ? 'Show imports from any time' : `Show imports from the ${r.label.toLowerCase()}`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </span>
            <button
              type="button"
              onClick={() => setFilters((f) => ({ ...f, warningsOnly: !f.warningsOnly }))}
              aria-pressed={filters.warningsOnly}
              style={historyChipStyle(filters.warningsOnly)}
              title="Show only imports that recorded warnings"
            >
              ⚠ Has warnings
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setFilters(HISTORY_FILTERS_CLEARED)}
              disabled={!filtersActive}
              title="Clear all filters"
            >
              Reset filters
            </button>
            {filtersActive && (
              <span
                aria-live="polite"
                style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
              >
                Showing {filteredEvents.length} of {events.length} imports
              </span>
            )}
          </div>
        )}

        {historyQ.isLoading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>Loading install history…</div>
        ) : historyQ.isError ? (
          <div style={{ color: 'var(--danger-primary, #B3261E)', fontSize: 13, padding: '12px 0' }}>
            Couldn't load install history: {historyQ.error instanceof Error ? historyQ.error.message : 'unknown error'}
          </div>
        ) : events.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>
            No bundles installed yet. Import one above — or see{' '}
            <a href="/help/bundles" style={{ color: 'var(--accent-primary)' }}>the bundles help</a>{' '}
            for what bundles can carry.
          </div>
        ) : filteredEvents.length === 0 ? (
          // CTSK-318 — filters excluded everything. Distinct from the
          // "nothing installed" empty state above so the operator knows
          // the data exists and it's the filters hiding it.
          <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>
            No imports match the current filters.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filteredEvents.map((ev) => <InstalledHistoryRow key={ev.id} ev={ev} />)}
          </div>
        )}
      </details>
    </section>
  );
}

function InstalledHistoryRow({ ev }: { ev: BundleInstallEvent }) {
  const label = ev.bundleName ?? (ev.nest ? `${ev.nest.code} — ${ev.nest.name}` : 'Unnamed bundle');
  const who = ev.installedBy?.name ?? ev.installedBy?.email ?? ev.installedBy?.id ?? 'unknown';
  const initial = (ev.installedBy?.name ?? ev.installedBy?.email ?? '?').slice(0, 1).toUpperCase();

  return (
    <details
      style={{
        border: '1px solid var(--border-subtle)',
        borderRadius: 6,
        background: 'var(--surface-sunken)',
      }}
    >
      <summary
        style={{
          cursor: 'pointer',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          fontSize: 13,
          userSelect: 'none',
          listStyle: 'none',
        }}
      >
        <span aria-hidden="true" style={{ color: 'var(--text-muted)', fontSize: 11 }}>▸</span>
        <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</strong>
        {ev.bundleUid && (
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{ev.bundleUid}</code>
        )}
        <span
          title={ev.installedAt}
          style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}
        >
          {fmtRelative(ev.installedAt)}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)', fontSize: 12 }}>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: 'color-mix(in srgb, var(--accent-primary) 22%, var(--surface-raised))',
              color: 'var(--accent-primary)',
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            {initial}
          </span>
          {who}
        </span>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          {sectionCountsLine(ev)}
        </span>
        {ev.warnings.length > 0 && (
          <span
            title={ev.warnings.join('\n')}
            style={{ color: 'var(--warning-primary, #B78100)', fontSize: 12, whiteSpace: 'nowrap' }}
          >
            ⚠ {ev.warnings.length}
          </span>
        )}
      </summary>

      <div style={{ padding: '4px 12px 12px 28px', display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
        {ev.legacy && (
          <div style={{ color: 'var(--text-muted)' }}>
            Imported before 0.1.122 — section counts are real, but item names,
            bundle identity and warnings were not captured at import time.
          </div>
        )}

        {/* Section breakdown */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {HISTORY_SECTIONS.map(({ key, href }) => {
            const s = (ev.sections as Record<string, BundleInstallEvent['sections'][string] | undefined>)[key];
            if (!s) return null;
            const n = s.inserted + s.updated + s.skipped;
            if (n === 0 && s.errors.length === 0) return null;
            return (
              <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ minWidth: 130, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {href ? (
                    <a href={href} style={{ color: 'inherit' }} title={`Open ${SECTION_LABEL[key] ?? key}`}>
                      {SECTION_LABEL[key] ?? key} ↗
                    </a>
                  ) : (SECTION_LABEL[key] ?? key)}
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {s.inserted > 0 && <strong style={{ color: 'var(--accent-primary)' }}>+{s.inserted}</strong>}
                  {s.updated > 0 && <span> ~{s.updated}</span>}
                  {s.skipped > 0 && <span style={{ color: 'var(--text-muted)' }}> {s.skipped} skipped</span>}
                  {n === 0 && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </span>
                {s.names && s.names.length > 0 && (
                  <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                    {s.names.map((name) => (
                      <span
                        key={name}
                        style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 11, background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}
                      >
                        {name}
                      </span>
                    ))}
                    {s.namesTruncated && <span style={{ color: 'var(--text-muted)' }}>… (list truncated)</span>}
                  </span>
                )}
                {s.errors.length > 0 && (
                  <span style={{ color: 'var(--danger-primary, #B3261E)' }}>
                    {s.errors.map((e, i) => <div key={i}>{e}</div>)}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Warnings */}
        {ev.warnings.length > 0 && (
          <div style={{ color: 'var(--warning-primary, #B78100)' }}>
            {ev.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
          </div>
        )}

        {/* Raw metadata */}
        <div style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 11, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <span>schema v{ev.bundleSchemaVersion ?? '?'}</span>
          <span>{ev.dryRun ? 'dry-run preview' : 'applied import'}</span>
          {ev.nest && <span>nest/{ev.nest.slug}</span>}
          <span title="Audit row id">{ev.id}</span>
          <span title="Absolute install time">{ev.installedAt}</span>
        </div>
      </div>
    </details>
  );
}

function Notes() {
  return (
    <section
      style={{
        background: 'var(--surface-sunken)',
        padding: 16,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
        fontSize: 12,
        color: 'var(--text-muted)',
      }}
    >
      <strong>How bundles work</strong>
      <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
        <li>Bundles use the same JSON shape as the Vault starter pack (<code>seed/*.json</code>).</li>
        <li>Imports target the <strong>active org</strong>. Switch orgs first if you need to land somewhere else.</li>
        <li>Rules reference containers + tags by NAME. The importer resolves them to ULIDs against the bundle's items + the existing org's rows.</li>
        <li>Idempotent on name — re-importing the same bundle skips existing rows; new ones land.</li>
        <li>Always preview first when applying an unfamiliar bundle.</li>
      </ul>
    </section>
  );
}
